import type { SolarTower } from './solarScene';
import type { GroundCrop } from '../solar/solarRig';

export interface SitePlanFloorGroup {
  id: string;
  label: string;
  from: number;
  to: number;
  /** Diagram rectangle, in percentages of the original sheet. */
  crop: GroundCrop;
  /** Common core position in original-sheet percentages. */
  anchorX: number;
  anchorY: number;
  /** Scale relative to the base diagram. Keep uniform to preserve shapes. */
  scale: number;
  /** Expected stacks for a separately uploaded diagram. */
  stackLabels?: string[];
  /** Multi-image groups own their diagram instead of using the project's 2D sitemap. */
  diagram?: { imageUrl: string; storagePath: string; uploadedAt?: string };
  /** Compass north for this diagram. Null means 3D works but its solar study is disabled. */
  northAngle?: number | null;
  /** Optional reviewed stack centres, overriding the original plotted section. */
  plots?: Array<{ label: string; x: number; y: number; width: number; depth: number;
    /** Reviewed boundary in original-image percentages, without a repeated closing point. */
    outline?: Array<{ x: number; y: number }>; blockNumber?: string }>;
}
export interface SitePlanFloorGroups {
  version: 1 | 2;
  mode?: 'outlines' | 'multi-image';
  groups: SitePlanFloorGroup[];
}

/** Outline-only corrections preserve the original tower heights, units and locations. */
export function applyStackOutlines(towers: SolarTower[], config: SitePlanFloorGroups | null): SolarTower[] {
  if (config?.mode !== 'outlines') return towers;
  return towers.map(tower => ({ ...tower, outline: config.groups[0]?.plots?.find(p =>
    Number(p.label) === Number(tower.label) && (p.blockNumber ?? '') === (tower.blockNumber ?? ''))?.outline }));
}

/** Draft alignment from the supplied sheets. An admin reviews against the live image. */
export function draftFloorGroups(name?: string | null): SitePlanFloorGroups | null {
  const key = (name ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
  if (key === 'THE SERRA RESIDENCES') return { version: 1, groups: [
    { id: 'lower', label: 'Levels 4–16', from: 4, to: 16, crop: { x: 61, y: 37, w: 23, h: 26 }, anchorX: 76.5, anchorY: 50, scale: 1 },
    { id: 'upper', label: 'Levels 18–27', from: 18, to: 27, crop: { x: 61, y: 70, w: 23, h: 26 }, anchorX: 76.5, anchorY: 83, scale: 1 },
    { id: 'top', label: 'Level 28', from: 28, to: 28, crop: { x: 61, y: 5, w: 23, h: 26 }, anchorX: 76.5, anchorY: 18, scale: 1 },
  ] };
  if (key === 'PROMENADE PEAK') return { version: 1, groups: [
    { id: 'lower', label: 'Levels 2–42', from: 2, to: 42, crop: { x: 6, y: 8, w: 46, h: 80 }, anchorX: 23, anchorY: 39, scale: 1 },
    { id: 'upper', label: 'Levels 44–63', from: 44, to: 63, crop: { x: 54, y: 8, w: 45, h: 80 }, anchorX: 70.5, anchorY: 39, scale: 1 },
  ] };
  if (key === 'AUREA') return { version: 2, mode: 'multi-image', groups: [
    { id: 'lower', label: 'Levels 4–32', from: 4, to: 32, crop: { x: 0, y: 0, w: 100, h: 100 },
      anchorX: 50, anchorY: 50, scale: 1, stackLabels: ['31', '32', '33', '34', '35', '36'], plots: [], northAngle: null },
    { id: 'upper', label: 'Levels 34–42', from: 34, to: 42, crop: { x: 0, y: 0, w: 100, h: 100 },
      anchorX: 50, anchorY: 50, scale: 1, stackLabels: ['31', '32'], plots: [], northAngle: null },
    { id: 'top', label: 'Level 43', from: 43, to: 43, crop: { x: 0, y: 0, w: 100, h: 100 },
      anchorX: 50, anchorY: 50, scale: 1, stackLabels: ['31', '32'], plots: [], northAngle: null },
  ] };
  return null;
}

export function isMultiImageFloorGroups(config: SitePlanFloorGroups | null | undefined): boolean {
  return config?.version === 2 && config.mode === 'multi-image';
}

export function replaceFloorGroupDiagram(config: SitePlanFloorGroups, groupId: string,
  diagram: NonNullable<SitePlanFloorGroup['diagram']>): SitePlanFloorGroups {
  return { ...config, groups: config.groups.map(group => group.id === groupId ? {
    ...group, diagram, crop: { x: 0, y: 0, w: 100, h: 100 }, anchorX: 50, anchorY: 50,
    scale: 1, plots: [], northAngle: null,
  } : group) };
}

/**
 * Put the selected diagram first because the scene uses group zero as its
 * ground: its crop (or its own diagram) is the plate the levels stand on, and
 * its coordinates are the scene's. Cropped groups need this as much as
 * multi-image ones, or every level is drawn on the first group's diagram.
 */
export function floorGroupsForScene(config: SitePlanFloorGroups | null, selected: string): SitePlanFloorGroups | null {
  if (!config || config.mode === 'outlines') return config;
  const active = config.groups.find(group => group.id === selected);
  return active ? { ...config, groups: [active, ...config.groups.filter(group => group.id !== selected)] } : config;
}

export function floorGroupInventoryWarnings(towers: SolarTower[], config: SitePlanFloorGroups): string[] {
  if (!isMultiImageFloorGroups(config)) return [];
  const availableFloors = (group: SitePlanFloorGroup) => new Set(towers
    .filter(tower => !group.stackLabels?.length || group.stackLabels.some(label => Number(label) === Number(tower.label)))
    .flatMap(tower => tower.units?.flatMap(unit => unit.floor == null ? [] : [unit.floor]) ?? []));
  return config.groups.flatMap(group => {
    const floors = availableFloors(group);
    const missing = Array.from(new Set([group.from, group.to])).filter(floor => !floors.has(floor));
    return missing.length ? [`${group.label}: no inventory units found on ${missing.map(f => `level ${f}`).join(' or ')}.`] : [];
  });
}

export function groupForTower(tower: Pick<SolarTower, 'x' | 'y'>, config: SitePlanFloorGroups): SitePlanFloorGroup | undefined {
  return config.groups.find(({ crop }) => tower.x >= crop.x && tower.x <= crop.x + crop.w
    && tower.y >= crop.y && tower.y <= crop.y + crop.h);
}

export function alignFloorGroupPoint(point: { x: number; y: number }, group: SitePlanFloorGroup, base: SitePlanFloorGroup) {
  return {
    x: (base.anchorX + (point.x - group.anchorX) * group.scale - base.crop.x) / base.crop.w * 100,
    y: (base.anchorY + (point.y - group.anchorY) * group.scale - base.crop.y) / base.crop.h * 100,
  };
}

/**
 * Some split-floor projects store the lower and upper parts of one physical
 * stack under separate block records. Rejoin those records by their real floor
 * number before building the uniform 3D stack.
 */
export function mergeFloorGroupStackUnits<T>(sources: Array<Array<T | null>>, floorOf: (unit: T) => number): T[] {
  const byFloor = new Map<number, T>();
  sources.forEach(source => source.forEach(unit => {
    if (unit == null) return;
    const floor = floorOf(unit);
    if (Number.isFinite(floor) && !byFloor.has(floor)) byFloor.set(floor, unit);
  }));
  return Array.from(byFloor.entries()).sort(([a], [b]) => a - b).map(([, unit]) => unit);
}

/** Extend the selected diagram uniformly through the building, preserving unit indices. */
export function floorGroupTowers(towers: SolarTower[], config: SitePlanFloorGroups, selected: string): SolarTower[] {
  const result: SolarTower[] = [];
  const seen = new Set<string>();
  const sources = config.groups.filter(group => group.id === selected).flatMap(group => group.plots
    ? group.plots.flatMap(plot => {
      const source = towers.find(t => Number(t.label) === Number(plot.label));
      return source ? [{ ...source, ...plot, x: plot.x - plot.width / 2, y: plot.y - plot.depth / 2, floorGroupId: group.id }] : [];
    })
    : config.version === 2 ? []
      : towers.filter(t => groupForTower(t, config)?.id === group.id).map(t => ({ ...t, floorGroupId: group.id })));
  for (const tower of sources) {
    const group = config.groups.find(g => g.id === tower.floorGroupId);
    if (!group) continue;
    const key = `${group.id}:${tower.blockNumber ?? ''}:${tower.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const indices = new Map<number, number>();
    tower.units?.forEach((unit, index) => { if (unit.floor != null) indices.set(unit.floor, index); });
    const active = group.id === selected;
    const units = Array.from({ length: group.to - group.from + 1 }, (_, index) => {
      const floor = group.from + index;
      const sourceIndex = indices.get(floor);
      const unit = sourceIndex == null ? undefined : tower.units?.[sourceIndex];
      return unit ? { ...unit, color: active ? unit.color : '#73777d', emphasis: active && unit.emphasis }
        : { floor, color: '#555960', emphasis: false, empty: true };
    });
    result.push({ ...tower, floorGroupId: group.id, baseFloor: group.from - 1,
      floors: units.length, topFloor: group.to, units, interactive: active,
      visible: active ? tower.visible : true,
      color: active ? tower.color : '#73777d', blinking: active && tower.blinking,
      onUnitClick: (index, element, x, y) => {
        const original = indices.get(group.from + index);
        if (active && original != null) tower.onUnitClick?.(original, element, x, y);
      },
    });
  }
  // Grey context uses the selected section's exact footprint at every level.
  // It carries no units or interactions from the other diagrams.
  const topFloor = Math.max(0, ...config.groups.map(group => group.to));
  for (const tower of [...result]) {
    const group = config.groups.find(g => g.id === tower.floorGroupId)!;
    const addContext = (baseFloor: number, floors: number) => {
      if (floors <= 0) return;
      result.push({ ...tower, baseFloor, floors, topFloor: baseFloor + floors,
        units: undefined, interactive: false, onClick: () => {}, onUnitClick: undefined,
        color: '#555960', blinking: false, visible: true,
      });
    };
    addContext(0, group.from - 1);
    addContext(group.to, topFloor - group.to);
  }
  return result;
}
